import { useContext, useEffect, useState } from 'react';

import { DarkmodeContext } from '@/components/Header/DarkmodeToggle';

/**
 * This hook returns a boolean value whether the dark mode theme is currently displayed
 * or not.
 *
 * IMPORTANT:
 * This hook should only control styling on components which are rendered after first load.
 * This hook is evaluated AFTER hydration, which means that you WILL introduce color flashes
 * if you are using this on components that are shown on load
 */
export function useIsDarkmode() {
  const { theme } = useContext(DarkmodeContext);
  const [isDarkMode, setIsDarkmode] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false,
  );

  useEffect(() => {
    if (theme === 'dark') {
      setIsDarkmode(true);
      return;
    }
    if (theme === 'light') {
      setIsDarkmode(false);
      return;
    }

    if (theme === 'system') {
      const query = window.matchMedia('(prefers-color-scheme: dark)');

      setIsDarkmode(query.matches);

      const listener = (e: MediaQueryListEvent) => {
        setIsDarkmode(e.matches);
      };

      query.addEventListener('change', listener);

      return () => {
        query.removeEventListener('change', listener);
      };
    }
  }, [theme]);

  return isDarkMode;
}
